import { useState } from 'react';
import { api } from '../../lib/api';
import { Pill } from '../../components/ui';

// 序列号独立模块（T45 前置）
// 查询 / 合同归属 / 溯源入口；同号多件候选并列，禁止合并；无序列号走内部编号

type SerialCandidate = {
  id: string;
  customer_serial?: string;
  internal_code?: string;
  asset_type?: string;
  customer_code?: string;
  customer_name?: string;
  item_code?: string;
  item_name_zh?: string;
  status?: string;
  current_version?: number;
  has_serial?: boolean;
  identity?: string;
  contracts?: { id: string; number: string; status?: string; contract_date?: string }[];
  primary_contract?: { number: string } | null;
  service_cycles?: { version: number; status?: string; intake_date?: string }[];
  latest_delivery?: { number: string; status?: string } | null;
  trace_url?: string;
};

type SerialQueryResult = {
  query: Record<string, string | null>;
  match_count: number;
  needs_resolution: boolean;
  merged: boolean;
  candidates: SerialCandidate[];
};

export function SerialLookupPage() {
  const [q, setQ] = useState('');
  const [internalCode, setInternalCode] = useState('');
  const [data, setData] = useState<SerialQueryResult | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [traceAsset, setTraceAsset] = useState<string | null>(null);
  const [trace, setTrace] = useState<any>(null);

  const search = async () => {
    setLoading(true);
    setError('');
    setData(null);
    setTrace(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('serial', q.trim());
      if (internalCode.trim()) params.set('internal_code', internalCode.trim());
      if (!q.trim() && !internalCode.trim()) {
        setError('请输入序列号或内部工件编号');
        setLoading(false);
        return;
      }
      const res = await api.get<{ data: SerialQueryResult }>(`/api/serials?${params}`);
      setData(res.data);
      if (!res.data?.match_count) setError('未找到匹配工件');
    } catch (e: any) {
      setError(e?.message || '查询失败');
    }
    setLoading(false);
  };

  const openTrace = async (id: string) => {
    setTraceAsset(id);
    try {
      const res = await api.get<{ data: any }>(`/api/physical-assets/${id}/trace`);
      setTrace(res.data);
    } catch {
      setTrace(null);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 960 }}>
      <h2 style={{ margin: '0 0 4px' }}>序列号查询</h2>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>
        独立序列号模块：查工件、合同归属与溯源。同客户同号多件并列显示，<b>不合并</b>；
        无序列号可用内部工件编号。身份以 <code>physical_asset.id</code> 为准。
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="序列号，如 TRACE-77"
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 200 }}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <input
          value={internalCode}
          onChange={(e) => setInternalCode(e.target.value)}
          placeholder="内部工件编号 / asset id"
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 200 }}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <button
          type="button"
          onClick={search}
          disabled={loading}
          style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: '#0d3b66', color: '#fff', cursor: 'pointer' }}
        >
          {loading ? '查询中…' : '查询'}
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {data && data.match_count > 0 && (
        <>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#475569' }}>
            命中 <b>{data.match_count}</b> 个候选
            {data.needs_resolution && (
              <span style={{ marginLeft: 8 }}>
                <Pill tone="warn" label="同号多件 · 请人工判定，禁止合并" />
              </span>
            )}
          </div>
          {data.candidates.map((c) => (
            <div
              key={c.id}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 10, background: '#fff',
              }}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                <strong>{c.customer_serial || '(无序列号)'}</strong>
                <span style={{ color: '#64748b', fontSize: 12 }}>身份 {c.id.slice(0, 8)}…</span>
                {c.status && <Pill tone="info" label={c.status} />}
                {c.current_version != null && <Pill tone="ok" label={`V${c.current_version}`} />}
                {!c.has_serial && <Pill tone="warn" label="内部编号" />}
              </div>
              <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.7 }}>
                <div>客户：{c.customer_name || '-'}（{c.customer_code || '-'}）</div>
                <div>物料：{c.item_name_zh || '-'}（{c.item_code || '-'}）</div>
                <div>
                  合同归属：
                  {c.contracts?.length
                    ? c.contracts.map((x) => x.number).join('、')
                    : '（暂无强关联合同）'}
                </div>
                <div>
                  维修周期：
                  {c.service_cycles?.length
                    ? c.service_cycles.map((v) => `V${v.version}(${v.status || '-'})`).join('、')
                    : '-'}
                </div>
                <div>最近交货：{c.latest_delivery ? `${c.latest_delivery.number} · ${c.latest_delivery.status}` : '-'}</div>
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => openTrace(c.id)}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}
                >
                  查看溯源
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {traceAsset && (
        <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>溯源 · {traceAsset.slice(0, 8)}…</h3>
          {!trace && <div style={{ color: '#64748b', fontSize: 12 }}>加载中…</div>}
          {trace && (
            <div style={{ fontSize: 12.5 }}>
              {(trace.cycles || trace.history || []).map((cy: any) => (
                <div key={cy.version} style={{ marginBottom: 10 }}>
                  <strong>V{cy.version}</strong> {cy.status || ''} {cy.intake_date || ''}
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {(cy.events || []).slice(0, 12).map((ev: any, i: number) => (
                      <li key={i}>{ev.date || ''} · {ev.label}{ev.detail ? ` — ${ev.detail}` : ''}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
